import { sameMode, SeasonalTokenOverrides, SeasonConfig } from '../types';
import { BackgroundTint } from './background-tint';
import { FallingSnow } from './falling-snow';
import winterSceneBg from './images/background.png';
import WinterLogoDark from './images/logo-dark.svg';
import WinterLogoLight from './images/logo-light.svg';
import wreathSvg from './images/wreath.svg';
import MorningLightMusic from './sounds/MorningLightMusic.mp3';

/**
 * Winter Holiday Theme - Semantic Token Overrides
 *
 * Forces dark mode, so both default and _dark resolve to the same values.
 * Deep navy backgrounds with icy blue accents.
 */
const winterTokenOverrides: SeasonalTokenOverrides = {
	colors: {
		primary: {
			fg: sameMode('#3b82f6'),
			fgStrong: sameMode('#2563eb'),
			fgStronger: sameMode('#1d4ed8'),
			fgStrongest: sameMode('#1e40af'),
			bg: sameMode('#3b82f6'),
			bgHover: sameMode('#2563eb'),
			bgActive: sameMode('#1d4ed8'),
			bgAlpha: sameMode('rgba(59, 130, 246, 0.15)'),
			bgAlphaHover: sameMode('rgba(59, 130, 246, 0.25)'),
			bgAlphaActive: sameMode('rgba(59, 130, 246, 0.35)'),
		},
		bg: {
			canvas: sameMode('transparent'),
			surface: sameMode('#111827'),
			soft: sameMode('#1e293b'),
			subtle: sameMode('#1a2234'),
			topNav: sameMode('#0f172a'),
			inset: sameMode('#0d1424'),
		},
		text: {
			interactive: sameMode('#60a5fa'),
		},
		border: {
			default: sameMode('#334155'),
			soft: sameMode('#1e293b'),
		},
	},
};

export const winterConfig: SeasonConfig = {
	themeClassName: 'winter-theme',
	toggleEmoji: '❄️',

	logoLight: WinterLogoLight,
	logoDark: WinterLogoDark,

	modalSound: MorningLightMusic,
	modalSoundVolume: 0.1,

	semanticTokenOverrides: winterTokenOverrides,

	buttonTexts: ['❄️ Spread winter cheer', '⛄ Send winter warmth', '🎁 Deliver appreciation', '✨ Share festive kudos', '🌟 Give a warm shoutout'],

	modalDecorations: [
		{ emoji: '🎄', top: '10px', left: '20px', fontSize: '40px', opacity: 0.2, rotation: -15 },
		{ emoji: '❄️', top: '30px', right: '30px', fontSize: '30px', opacity: 0.15, rotation: 15 },
		{ emoji: '⛄', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '✨', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
		{ emoji: '🎁', bottom: '60px', right: '40px', fontSize: '22px', opacity: 0.12, rotation: 12 },
	],

	modalColors: {
		overlayBg: 'blackAlpha.700',
		contentBg: '#0a0f1a',
		contentBgImage: `linear-gradient(180deg, rgba(10, 15, 30, 0.85) 0%, rgba(15, 25, 50, 0.75) 50%, rgba(10, 15, 30, 0.9) 100%), url("${winterSceneBg}")`,
		contentBgSize: 'cover, 100% auto',
		contentBgPosition: 'center, center bottom',
		contentBgRepeat: 'no-repeat, no-repeat',
		borderColor: 'whiteAlpha.200',
		boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
		logoDropShadowColor: 'rgba(100, 150, 255, 0.4)',
		subtitleColor: 'blue.200',
		enableButtonBg: 'white',
		enableButtonHoverBg: 'gray.100',
		enableButtonActiveBg: 'gray.200',
		enableButtonBoxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)',
		enableButtonHoverBoxShadow: '0 6px 20px rgba(255, 255, 255, 0.3)',
		enableButtonTextColor: 'blue.800',
		cancelButtonColor: 'blue.300',
		cancelButtonHoverColor: 'blue.200',
		noticeBg: 'rgba(30, 58, 138, 0.5)',
		noticeBorderColor: 'blue.600',
		noticeTextColor: 'blue.200',
	},

	modalText: {
		subtitle: 'Celebrate the season with our festive winter theme!',
		features: [
			{
				emoji: '❄️',
				text: 'Gentle snowfall with snowflakes drifting across your screen',
			},
			{
				emoji: '✨',
				text: 'A cozy winter wonderland aesthetic with icy blues throughout the interface',
			},
			{
				emoji: '🎁',
				text: 'Recognition you receive through Dec. 31st arrives gift-wrapped — unwrap to reveal!',
			},
		],
		endDateNotice: 'This festive theme will be available through the holiday season. Toggle it on/off anytime!',
		enableButtonText: 'Enable Winter Theme',
		cancelButtonText: 'Maybe Later',
	},

	EffectsComponent: () => (
		<>
			<BackgroundTint />
			<FallingSnow />
		</>
	),

	avatarOverlay: {
		image: wreathSvg,
		// Square viewBox (56 × 56). The artwork is wrapped in a
		// `transform: translate(0, 5)` group, shifting the wreath ring's
		// center from the geometric (28, 28) to roughly (28, 29.5) — i.e.
		// (50%, 53%) of the viewBox.
		scale: 1.4,
		anchor: { x: 0.5, y: 0.53 },
	},
};
